import React from 'react';
import {Dimensions, ScrollView, View} from 'react-native';
import {Icon, makeStyles} from '@rn-vui/themed';

import BackButton from '../../../components/rewards/BackButton';
import Button from '../../../components/themed/Button';
import ActivityIndicator from '../../../components/ui-controls/ActivityIndicator';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Markdown from '../../../components/ui-controls/Markdown';
import PressableWithOpacityFeedback from '../../../components/ui-controls/PressableWithOpacityFeedback';
import {AppRoute} from '../../../models/AppRoute';
import {InformationContentType, RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import * as RootNavigation from '../../../navigation/RootNavigation';
import {useStores} from '../../../stores/use-stores';
import apiService from '../../../transport/apiService';
import {useAirtableInformation} from '../TermsAndConditions';

const useStyles = makeStyles(theme => ({
  icon: {color: theme.colors?.legacy?.uiControls?.primary},
}));

export default function RewardsActivateTermsAndConditionsScreen({
  route,
  navigation,
}: RouteNavigatorScreenProps<AppRoute.REWARDS_ACTIVATE_TERMS_AND_CONDITIONS>) {
  const {isLoading, information} = useAirtableInformation(
    InformationContentType.REWARDS_PROGRAM_TERMS_AND_CONDITIONS,
  );

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const {userStore, rewardsStore, cartStore} = useStores();
  const phoneNumber = userStore.phoneNumber || '';

  const handleFinishSigningUp = async () => {
    setIsSubmitting(true);

    // Check if user already has a rewards card (e.g. from prior lite sign-up)
    // If so, skip the opt-in verification — they already have a card
    await rewardsStore.loadRewardsForUser(phoneNumber);
    if (rewardsStore.hasRewards) {
      // Reload cart to apply rewards pricing
      cartStore.fullyReloadOrderFromMFL();
      navigation.navigate(AppRoute.REWARDS_ACTIVATE_SUCCESS, {flow: 'activate'});
      setIsSubmitting(false);
      return;
    }

    if (await apiService.rewardsVerifyExistingUserPhoneNumber({phoneNumber})) {
      navigation.navigate(AppRoute.REWARDS_ACTIVATE_VERIFICATION, route.params);
    }
    setIsSubmitting(false);
  };

  const styles = useStyles();

  return (
    <ContentView testID="rewards-activate-terms-and-conditions-screen">
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10,
        }}>
        <BackButton
          onPress={() => {
            if (navigation.canGoBack()) {
              navigation.goBack();
            } else {
              // Navigate from ROOT_REWARDS_FLOW to ROOT > MAIN > HOME
              RootNavigation.navigate(AppRoute.ROOT, {
                screen: AppRoute.MAIN,
                params: {screen: AppRoute.HOME},
              });
            }
          }}
        />
        <PressableWithOpacityFeedback
          onPress={() => {
            if (navigation.canGoBack()) {
              navigation.goBack();
            } else {
              // Navigate from ROOT_REWARDS_FLOW to ROOT > MAIN > HOME
              RootNavigation.navigate(AppRoute.ROOT, {
                screen: AppRoute.MAIN,
                params: {screen: AppRoute.HOME},
              });
            }
          }}>
          <Icon
            type="material-icons"
            name="close"
            color={styles.icon.color}
            // style={styles.icon}
            size={36}
          />
        </PressableWithOpacityFeedback>
      </View>
      {!!isLoading && <ActivityIndicator />}
      {!!information?.content && (
        <>
          <ScrollView
            style={{
              paddingBottom: 40,
              height: Dimensions.get('window').height - 150,
            }}>
            <Markdown>{information.content}</Markdown>
          </ScrollView>
          <View style={{flex: 1}} />
          <View
            style={{
              backgroundColor: 'white',
              paddingVertical: 20,
              paddingHorizontal: 10,
              bottom: 0,
              left: 0,
              right: 0,
            }}>
            <Button
              loading={isSubmitting}
              disabled={isSubmitting}
              variant="outlined"
              onPress={handleFinishSigningUp}>
              AGREE AND FINISH SIGNING UP
            </Button>
          </View>
        </>
      )}
    </ContentView>
  );
}
